// Step 4's paywall, both arms — the MOCK's. A real build's plan screen is the
// membership site's paywall (docs/30 §6), reached from step 4, so a real build
// that lands here is sent to step 4.
//
// This page is variant B's ENTRY screen and variant A's second screen, so the
// membership check runs here too: anyone already entitled — US talent, an
// existing Pro, an active trial — never sees a paywall and is forwarded to
// review. That check is the reason this page can be reached directly without
// leaking a payment prompt to someone who owes nothing.
//
// The two arms differ only in presentation: PaywallTrialOffer (Figma 19) and
// PaywallProOffer (the ported promo-offer-1). Everything else on this page —
// the check, the offer fetch, the checkout call, the failure modal — is shared,
// because an A/B test of two paywalls is worthless if the plumbing differs too.

import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import styled from "styled-components";
import i18next from "i18next";
import { iRootState } from "../../../store";
import SubmissionLayout from "../common/SubmissionLayout";
import SubmissionSheet from "../common/SubmissionSheet";
import { monthDay } from "../common/dates";
import PaywallTrialOffer from "../common/PaywallTrialOffer";
import PaywallProOffer from "../common/PaywallProOffer";
import { canSend } from "../../../models/submissionStep4Model";
import { paysFirst, resolveStep4Variant } from "../common/step4Variant";
import { parseSubmissionParams, withParam } from "../common/submissionParams";
import { isMockApi } from "../../../shared/api/submission";
import { isSubmissionAuthed } from "../../../helpers/submissionAuth";
import { fallbackTo, hasFlowContext } from "../common/flowGuard";
import { trackSubmissionEvent } from "../analytics";
import { membershipPrimary } from "../common/membershipButton";

interface Props extends StateProps, DispatchProps {
  history: any;
  location: any;
}

const REVIEW_PATH = "/submission/flow/step/4";

const PaywallPage: React.FC<Props> = ({
  history,
  location,
  breakdownId,
  submissionInfo,
  sessionId,
  draft,
  membership,
  offer,
  selectedServiceId,
  checkoutPhase,
  checkoutError,
  loading,
  loadMembership,
  loadOffer,
  chooseService,
  startCheckout,
  holdSubmission,
  recordExposure,
  clearCheckoutError,
  refreshGate,
  ensureDraft,
}) => {
  const [ignoring, setIgnoring] = useState(false);
  const [held, setHeld] = useState(false);
  const [ready, setReady] = useState(false);
  const submissionId = draft ? draft.submissionId : 0;
  const variant = resolveStep4Variant(location.search);
  // The breakdown's own deadline: nothing can be kept past the point where
  // sending it would achieve anything, so the sheet promises exactly that day.
  // Past the deadline (late) the hold names no date: the margin's end is never shown.
  const holdUntil = submissionInfo && !submissionInfo.isLate ? monthDay(submissionInfo.closingDateUtc) : "";

  useEffect(() => {
    if (!isMockApi()) {
      history.replace(`${REVIEW_PATH}${location.search}`);
      return;
    }
    if (!hasFlowContext({ search: location.search, breakdownId })) {
      history.replace(fallbackTo(location.search));
      return;
    }
    if (!isSubmissionAuthed()) {
      history.replace(`/submission/flow/verify-email${location.search}`);
      return;
    }
    // The deadline first: no paywall for a job that has closed, and certainly
    // no checkout session for one.
    refreshGate({ history, search: location.search });

    const boot = async () => {
      // A full page load empties the store, and checkout needs the submission:
      // it comes back from the server (createDraft is idempotent per breakdown).
      if (!draft || !draft.submissionId) {
        const params = parseSubmissionParams(location.search);
        await ensureDraft({
          breakdownId: breakdownId || params.bd || null,
          roleId: params.roleid || null,
        });
      }
      const current = await loadMembership();
      // Nothing to sell. Forward rather than render — a visitor who owes nothing
      // must never see a price.
      if (canSend(current)) {
        trackSubmissionEvent("sf_step4_paywall_skipped", {
          variant,
          reason: current ? current.reason : "unknown",
        });
        history.replace(`${REVIEW_PATH}${location.search}`);
        return;
      }
      await loadOffer(variant);
      setReady(true);
      trackSubmissionEvent("sf_step4_paywall_viewed", { variant });
      // Variant B's entry screen logs its own exposure; in variant A the review
      // screen already did, and the once-per-session guard makes this a no-op.
      recordExposure({ variant, sessionId, breakdownId, roleId: draft ? draft.roleId : null });
    };
    boot();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onCheckout = async () => {
    if (!submissionId) return;
    const resp = await startCheckout({
      variant,
      submissionId,
      // Both arms come back to review: it is the last confirm before sending,
      // and in variant B it is the screen the visitor has not seen yet.
      // With the query string: bd/roleid must survive the round trip through
      // Stripe, or the review page comes back without the breakdown it is
      // about and every link out of it drops the refs.
      returnPath: `${REVIEW_PATH}${location.search}`,
    });
    if (resp && resp.redirectUrl) {
      trackSubmissionEvent("sf_step4_checkout_started", {
        variant,
        serviceId: selectedServiceId,
        attemptId: resp.attemptId,
      });
      // Same-window navigation, never a popup: a blocked popup would leave the
      // visitor staring at an unchanged page with no idea what happened.
      window.location.assign(resp.redirectUrl);
    }
  };

  // Variant A's "Ignore trial" → the gate copy, then where the membership paywall's Lite goes:
  // the saved page with flow=submission, which forwards to step 4's back-without-paying
  // screen (it holds the submission there).
  const onEmailReminder = async () => {
    if (!submissionId) return;
    trackSubmissionEvent("sf_step4_reminder_requested", { variant });
    setHeld(true);
    history.push(`/submission/saved${withParam(withParam(location.search, "from", "trial"), "flow", "submission")}`);
  };

  // Variant B only. After a payment that failed or was cancelled, the workflow
  // still has to be finishable — otherwise a declined card is a dead end and the
  // demo cannot reach the end of the funnel. Held and parked, as the membership
  // paywall's "Continue with Lite version" does: step 4 would only send a
  // test-B actor who still owes a plan straight back here.
  const onContinueWithoutPro = async () => {
    trackSubmissionEvent("sf_step4_continued_without_pro", { variant });
    clearCheckoutError();
    await onEmailReminder();
  };

  const failureCopy = (code?: string): string => {
    if (code === "cardDeclined") {
      return i18next.t("Your card was declined, so nothing was charged.");
    }
    if (code === "checkoutSessionExpired") {
      return i18next.t("That checkout expired before it was finished.");
    }
    return i18next.t("The payment didn't go through, so nothing was charged.");
  };

  const busy = checkoutPhase === "creating" || checkoutPhase === "redirecting";

  return (
    <SubmissionLayout step={4}>
      {/* Back from a failed or cancelled checkout. Shown over the paywall, so
          the visitor lands where they can try again rather than on a dead end. */}
      {checkoutError && (
        <SubmissionSheet
          title={i18next.t("That payment didn't complete")}
          onDismiss={clearCheckoutError}
        >
          <SheetText>{failureCopy(checkoutError.code)}</SheetText>
          <PrimaryButton onClick={clearCheckoutError}>
            {i18next.t("Try again")}
          </PrimaryButton>
          {paysFirst(variant) && (
            <TextButton type="button" onClick={onContinueWithoutPro} disabled={!submissionId || loading}>
              {i18next.t("Continue without PRO")}
            </TextButton>
          )}
        </SubmissionSheet>
      )}

      {/* Variant A's Figma 19.1 sheet. It leads with what is kept, not with
          the rule: the visitor has just declined the trial, and the useful
          news is that their work is safe until a named day. The rule itself
          ("Only PRO members or Trial users…") stays with the server's refusal
          on send. */}
      {ignoring && (
        <SubmissionSheet
          title={i18next.t("We'll hold it for you")}
          onDismiss={() => setIgnoring(false)}
        >
          <SheetText>
            {holdUntil && submissionInfo
              ? i18next.t(
                  "Your submission for {{name}} is saved. Nothing's lost. Come back before {{date}} and it's still here.",
                  { name: submissionInfo.breakdownName, date: holdUntil }
                )
              : i18next.t(
                  "Your submission is saved. Nothing's lost. Come back before the deadline and it's still here."
                )}
          </SheetText>
          <PrimaryButton onClick={() => setIgnoring(false)}>
            {i18next.t("Actually, let's send it")}
          </PrimaryButton>
          <TextButton type="button" onClick={onEmailReminder} disabled={held || loading || !submissionId}>
            {held ? i18next.t("Reminder scheduled") : i18next.t("Email me a reminder")}
          </TextButton>
        </SubmissionSheet>
      )}

      {!ready || !offer ? (
        <Loading aria-busy="true">
          <Skeleton height={44} />
          <Skeleton height={220} />
          <Skeleton height={48} />
        </Loading>
      ) : paysFirst(variant) ? (
        <PaywallProOffer
          offer={offer}
          selectedServiceId={selectedServiceId}
          onSelectService={chooseService}
          onCheckout={onCheckout}
          busy={busy}
        />
      ) : (
        <PaywallTrialOffer
          offer={offer}
          selectedServiceId={selectedServiceId}
          onSelectService={chooseService}
          onCheckout={onCheckout}
          onIgnore={() => {
            trackSubmissionEvent("sf_step4_trial_ignored", { variant });
            setIgnoring(true);
          }}
          busy={busy}
        />
      )}
    </SubmissionLayout>
  );
};

const Loading = styled.div`
  padding: 16px 0;
`;

const Skeleton: any = styled.div`
  height: ${(p: any) => p.height || 16}px;
  border-radius: 8px;
  background: #eef1f5;
  margin-bottom: 12px;
`;

const SheetText = styled.p`
  font-size: 14px;
  color: #53627c;
  line-height: 1.5;
  margin: 0 0 4px;
`;

const PrimaryButton = styled.button`
  width: 100%;
  flex: none;
  ${membershipPrimary}
  padding: 14px 0;
  min-height: 48px;
`;

const TextButton = styled.button`
  display: block;
  width: 100%;
  flex: none;
  background: none;
  border: none;
  color: #0f9bf0;
  font-size: 14px;
  font-weight: 600;
  padding: 12px 0;
  cursor: pointer;
  min-height: 44px;

  &:disabled {
    color: #8a94a6;
    cursor: default;
  }
`;

interface StateProps {
  breakdownId: string | null;
  sessionId: string | null;
  draft: iRootState["submissionSignupModel"]["draft"];
  membership: iRootState["submissionStep4Model"]["membership"];
  offer: iRootState["submissionStep4Model"]["offer"];
  selectedServiceId: number | null;
  checkoutPhase: iRootState["submissionStep4Model"]["checkoutPhase"];
  checkoutError: iRootState["submissionStep4Model"]["checkoutError"];
  loading: boolean;
  submissionInfo: iRootState["submissionFlowModel"]["submissionInfo"];
}

interface DispatchProps {
  loadMembership: () => Promise<any>;
  loadOffer: (variant: any) => Promise<any>;
  chooseService: (serviceId: number) => Promise<any>;
  startCheckout: (payload: {
    variant: any;
    submissionId: number;
    returnPath: string;
  }) => Promise<any>;
  holdSubmission: (payload: { submissionId: number; request: any }) => Promise<any>;
  recordExposure: (payload: any) => Promise<any>;
  clearCheckoutError: () => void;
  refreshGate: (payload: { history: any; search?: string }) => Promise<any>;
  ensureDraft: (payload: { breakdownId: string | null; roleId: string | null }) => Promise<any>;
}

const mapStateToProps = (state: iRootState): StateProps => ({
  breakdownId: state.submissionFlowModel.breakdownId,
  submissionInfo: state.submissionFlowModel.submissionInfo,
  sessionId: state.submissionSignupModel.sessionId,
  draft: state.submissionSignupModel.draft,
  membership: state.submissionStep4Model.membership,
  offer: state.submissionStep4Model.offer,
  selectedServiceId: state.submissionStep4Model.selectedServiceId,
  checkoutPhase: state.submissionStep4Model.checkoutPhase,
  checkoutError: state.submissionStep4Model.checkoutError,
  loading: state.submissionStep4Model.loading,
});

const mapDispatchToProps = (dispatch: any): DispatchProps => ({
  loadMembership: dispatch.submissionStep4Model.loadMembership,
  loadOffer: dispatch.submissionStep4Model.loadOffer,
  chooseService: dispatch.submissionStep4Model.chooseService,
  startCheckout: dispatch.submissionStep4Model.startCheckout,
  holdSubmission: dispatch.submissionStep4Model.holdSubmission,
  recordExposure: dispatch.submissionStep4Model.recordExposure,
  clearCheckoutError: () => dispatch.submissionStep4Model.setCheckoutError(null),
  refreshGate: dispatch.submissionFlowModel.refreshGate,
  ensureDraft: dispatch.submissionSignupModel.ensureDraft,
});

export default connect(mapStateToProps, mapDispatchToProps)(PaywallPage);
